import { formatStringValue as formatValue } from "@/helpers/valueFormat"
import { useFormValidation } from "@/hooks/formValidation"
import * as connectPanelValue from "@/pages/CustomConnectPanel/value"

interface ContactProfileHookInput {
  currentCase: connectPanelValue.Decoder.CaseAndActivityFeeds | null
  historyItem: connectPanelValue.ActivityFeedEntry.ActivityFeedEntryType | null
  studentProfile: connectPanelValue.Decoder.StudentProfile | null
}

interface ContactProfileHookType {
  contactName: string
  contactNameKana: string
  historyEntries: connectPanelValue.ActivityFeedEntry.ActivityFeedEntryType[]
}

type SelectedQuestionsAndAnswers = {
  preferredCompanies?: connectPanelValue.Decoder.SelectedQuestion
  preferredIndustries?: connectPanelValue.Decoder.SelectedQuestion
  preferredJobTypes?: connectPanelValue.Decoder.SelectedQuestion
  selectedTextualQuestion?: connectPanelValue.Decoder.SelectedTextualQuestion
}

type ChannelDetailInformation = {
  channelTitle: string
  registrationDate: Date | null
} & SelectedQuestionsAndAnswers

const useContactProfileHook = ({
  currentCase,
  historyItem,
  studentProfile,
}: ContactProfileHookInput): ContactProfileHookType => {
  const { isKanji } = useFormValidation()

  const getNames = (): {
    contactName: string
    contactNameKana: string
  } => {
    const isJapaneseName = isKanji(
      studentProfile?.last_name || studentProfile?.first_name || "",
    )

    if (!isJapaneseName) {
      const contactName = `${studentProfile?.first_name ?? ""} ${
        studentProfile?.last_name ?? ""
      }`

      return {
        contactName: formatValue(contactName),
        contactNameKana: formatValue(null),
      }
    }

    const contactName = `${studentProfile?.last_name ?? ""}${
      studentProfile?.first_name ?? ""
    }`

    const contactNameKana = `${studentProfile?.last_name_kana ?? ""} ${
      studentProfile?.first_name_kana ?? ""
    }`

    return {
      contactName: formatValue(contactName),
      contactNameKana: formatValue(contactNameKana),
    }
  }

  const caseHistoryEntries = currentCase?.activity_feed ?? []
  const historyEntries = historyItem
    ? [historyItem, ...caseHistoryEntries]
    : caseHistoryEntries

  return {
    ...getNames(),
    historyEntries,
  }
}

function getLastQuestionAnswer<T>(questions: T[]) {
  // same question can be asked in multiple event forms with different answers
  return questions.at(-1)
}

function questionByTitle(
  questions: {
    answers: string[] | null
    question: string | null
  }[],
  title: string,
) {
  const similarQuestions = questions.filter(
    ({ question }) => question === title,
  )

  return getLastQuestionAnswer(similarQuestions)
}

function getSelectedChannelDetails(
  studentCase: connectPanelValue.Decoder.Case,
): ChannelDetailInformation[] {
  const studentChannels = studentCase?.processed_data.selected_channels

  return studentChannels
    .map((channel) => ({
      channelTitle: channel.channel_title,
      registrationDate: channel.registration_date,
      questionsAndAnswers: getChannelQuestionsAndAnswers(channel),
    }))
    .filter((channelData) => channelData.questionsAndAnswers !== null)
    .map((channelData) => ({
      ...channelData.questionsAndAnswers,
      channelTitle: channelData.channelTitle,
      registrationDate: channelData.registrationDate,
    }))
}

function getChannelQuestionsAndAnswers(
  channel: connectPanelValue.Decoder.SelectedChannel,
): SelectedQuestionsAndAnswers | null {
  const questions = channel.selected_questions ?? []
  const textualQuestions = channel.selected_textual_questions ?? []
  const preferredCompanies = questionByTitle(
    questions,
    "掲載企業の中で興味のある企業があれば教えてください。(複数回答可)",
  )
  const preferredIndustries = questionByTitle(
    questions,
    "興味のある業界を教えてください。　複数選択可",
  )
  const preferredJobTypes = questionByTitle(questions, "職種志望 2つまで選択可")
  const selectedTextualQuestion = getLastQuestionAnswer(textualQuestions)

  if (
    !preferredCompanies?.answers?.length &&
    !preferredIndustries?.answers?.length &&
    !preferredJobTypes?.answers?.length &&
    !selectedTextualQuestion?.answer
  ) {
    return null
  }

  return {
    preferredCompanies,
    preferredIndustries,
    preferredJobTypes,
    selectedTextualQuestion,
  }
}

export {
  type ChannelDetailInformation,
  getSelectedChannelDetails,
  useContactProfileHook,
}
